import { useState, useEffect } from 'react';
import { getISTDateString } from '../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getPayments, recordPayment, updatePayment, deletePayment, getGuests, getProperties, exportReceiptsReport, getOtherCategories, updateOtherCategories } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import { Banknote, Plus, CreditCard, Wallet, Info, Pencil, Trash2, Save, X, Download, TrendingUp, CalendarDays, Calendar, History, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Payments = () => {
  const { canAdd, canEdit, canDelete, canExport } = useAuth();
  const [payments, setPayments] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [properties, setProperties] = useState([]);
  
  // View mode and date filters
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(getISTDateString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const [form, setForm] = useState({
    guest_id: '',
    other_category: '',
    cash_other: 0,
    bank_other: 0,
    remarks: '',
    payment_date: getISTDateString()
  });
  const [otherCategories, setOtherCategories] = useState([]);
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [savingCats, setSavingCats] = useState(false);

  const [editForm, setEditForm] = useState({
    cash_rent: 0,
    cash_deposit: 0,
    cash_electricity: 0,
    cash_other: 0,
    bank_rent: 0,
    bank_deposit: 0,
    bank_electricity: 0,
    bank_other: 0,
    remarks: '',
    payment_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [viewMode, selectedDate, selectedMonth]);

  useEffect(() => {
    getOtherCategories().then(res => setOtherCategories(res.data.categories || [])).catch(() => {});
  }, []);

  const handleAddCategory = async () => {
    const name = newCat.trim().toUpperCase();
    if (!name) return;
    if (otherCategories.some(c => c.toUpperCase() === name)) { toast.error('Category already exists'); return; }
    try {
      setSavingCats(true);
      const res = await updateOtherCategories([...otherCategories, name]);
      setOtherCategories(res.data.categories || []);
      setNewCat('');
      toast.success('Category added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add category'); }
    finally { setSavingCats(false); }
  };

  const handleRemoveCategory = async (name) => {
    try {
      setSavingCats(true);
      const res = await updateOtherCategories(otherCategories.filter(c => c !== name));
      setOtherCategories(res.data.categories || []);
      if (form.other_category === name) setForm(f => ({ ...f, other_category: '' }));
      toast.success('Category removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove category'); }
    finally { setSavingCats(false); }
  };

  const fetchData = async () => {
    try {
      const params = viewMode === 'daily' ? { date: selectedDate } : 
                     viewMode === 'monthly' ? { month: selectedMonth } : 
                     { lifetime: true };
      const [paymentsRes, guestsRes, propsRes] = await Promise.all([
        getPayments(params),
        // Include joined, notice, and left (with dues) guests for payment collection
        getGuests({}),  // Fetch all guests, filter in display if needed
        getProperties()
      ]);
      setProperties(propsRes.data || []);
      // Map each guest to its property so receipts can be filtered property-wise
      const guestPropMap = {};
      (guestsRes.data || []).forEach(g => { guestPropMap[g.id] = { property_id: g.property_id, property_name: g.property_name }; });
      // Sort payments by date (most recent first)
      const sortedPayments = paymentsRes.data
        .map(p => ({ ...p, property_id: guestPropMap[p.guest_id]?.property_id || null, property_name: guestPropMap[p.guest_id]?.property_name || null }))
        .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
      setPayments(sortedPayments);
      // Include all guests except 'booked' (no payments needed for bookings)
      // Left guests with dues should be available for payment collection
      setGuests(guestsRes.data
        .filter(g => g.status !== 'booked')
        .map(g => ({
          ...g,
          room_info: g.room_info || `Room ${g.room_id}`
        })));
    } catch (err) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestSelect = (guestId) => {
    setForm({ ...form, guest_id: guestId === 'none' ? '' : guestId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const entries = [];
      if (form.cash_other > 0) entries.push({ type: 'other', mode: 'cash', amount: form.cash_other });
      if (form.bank_other > 0) entries.push({ type: 'other', mode: 'bank', amount: form.bank_other });

      if (entries.length === 0) {
        toast.error('Please enter a Cash or Bank amount');
        return;
      }
      if (!form.other_category) {
        toast.error('Please select a Category for this Other receipt');
        return;
      }
      if (!form.guest_id && !form.remarks.trim() && !form.other_category) {
        toast.error('Please add a Description/Remark for this Other receipt');
        return;
      }

      // Record each receipt separately
      for (const entry of entries) {
        await recordPayment({
          guest_id: form.guest_id || null,
          amount: entry.amount,
          payment_type: entry.type,
          payment_mode: entry.mode,
          other_category: form.other_category,
          remarks: form.remarks || form.other_category,
          payment_date: form.payment_date
        });
      }
      
      toast.success(`${entries.length} Other receipt(s) recorded successfully`);
      setDialogOpen(false);
      setForm({ 
        guest_id: '', 
        other_category: '',
        cash_other: 0,
        bank_other: 0,
        remarks: '', 
        payment_date: getISTDateString()
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record receipt');
    }
  };

  const handleEditClick = (payment) => {
    setEditingPayment(payment);
    // Head is LOCKED on edit. Only amount / mode / date / remarks can change within the same head.
    // To move money to a different head, delete this entry and record it again.
    setEditForm({
      amount: payment.amount || 0,
      payment_mode: payment.payment_mode || 'cash',
      remarks: payment.remarks || '',
      payment_date: payment.payment_date || getISTDateString()
    });
    setEditDialogOpen(true);
  };

  const _headLabel = (t) => {
    if (t === 'rent') return 'Rent';
    if (t === 'deposit' || t === 'booking_deposit') return 'Deposit';
    if (t === 'electricity') return 'Electricity';
    if (t === 'other') return 'Other';
    return (t || '').toUpperCase();
  };


  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const newAmount = parseFloat(editForm.amount) || 0;
      if (newAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      // Head stays the SAME as the original entry — only amount/mode/date/remarks change.
      await updatePayment(editingPayment.id, {
        amount: newAmount,
        payment_type: editingPayment.payment_type,
        payment_mode: editForm.payment_mode,
        remarks: editForm.remarks,
        payment_date: editForm.payment_date
      });
      toast.success('Receipt updated');
      setEditDialogOpen(false);
      setEditingPayment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update receipt');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = viewMode === 'daily' ? { date: selectedDate } : 
                     viewMode === 'monthly' ? { month: selectedMonth } : 
                     { lifetime: true };
      const response = await exportReceiptsReport(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const filename = viewMode === 'daily' ? `Receipts_${selectedDate}` :
                       viewMode === 'monthly' ? `Receipts_${selectedMonth}` :
                       'Receipts_Lifetime';
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Receipts Report downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Receipts Report');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this receipt? This will also remove the linked Daily Hisab entry if any.')) return;
    try {
      await deletePayment(paymentId);
      toast.success('Receipt deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const scopedPayments = selectedProperty === 'all'
    ? payments
    : payments.filter(p => p.property_id === selectedProperty);

  const totals = {
    cash: scopedPayments.filter(p => p.payment_mode === 'cash').reduce((sum, p) => sum + p.amount, 0),
    bank: scopedPayments.filter(p => p.payment_mode === 'bank').reduce((sum, p) => sum + p.amount, 0),
    rent: scopedPayments.filter(p => p.payment_type === 'rent').reduce((sum, p) => sum + p.amount, 0),
    deposit: scopedPayments.filter(p => p.payment_type === 'deposit').reduce((sum, p) => sum + p.amount, 0),
    electricity: scopedPayments.filter(p => p.payment_type === 'electricity').reduce((sum, p) => sum + p.amount, 0),
    other: scopedPayments.filter(p => p.payment_type === 'other').reduce((sum, p) => sum + p.amount, 0),
    cash_rent: scopedPayments.filter(p => p.payment_mode === 'cash' && p.payment_type === 'rent').reduce((sum, p) => sum + p.amount, 0),
    cash_deposit: scopedPayments.filter(p => p.payment_mode === 'cash' && p.payment_type === 'deposit').reduce((sum, p) => sum + p.amount, 0),
    cash_electricity: scopedPayments.filter(p => p.payment_mode === 'cash' && p.payment_type === 'electricity').reduce((sum, p) => sum + p.amount, 0),
    cash_other: scopedPayments.filter(p => p.payment_mode === 'cash' && p.payment_type === 'other').reduce((sum, p) => sum + p.amount, 0),
    bank_rent: scopedPayments.filter(p => p.payment_mode === 'bank' && p.payment_type === 'rent').reduce((sum, p) => sum + p.amount, 0),
    bank_deposit: scopedPayments.filter(p => p.payment_mode === 'bank' && p.payment_type === 'deposit').reduce((sum, p) => sum + p.amount, 0),
    bank_electricity: scopedPayments.filter(p => p.payment_mode === 'bank' && p.payment_type === 'electricity').reduce((sum, p) => sum + p.amount, 0),
    bank_other: scopedPayments.filter(p => p.payment_mode === 'bank' && p.payment_type === 'other').reduce((sum, p) => sum + p.amount, 0),
    total: scopedPayments.reduce((sum, p) => sum + p.amount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  const visiblePayments = scopedPayments.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (p.guest_name || '').toLowerCase().includes(q) || (p.room_info || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="receipts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Receipts</h1>
          <p className="text-slate-500 mt-1">Record and track guest payment receipts</p>
        </div>
        <div className="flex gap-2">
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleExportExcel}
            data-testid="export-receipts-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {canAdd('receipts') && (
              <DialogTrigger asChild>
                <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-payment-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Record Receipt
                </Button>
              </DialogTrigger>
            )}
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Other Receipt</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                For miscellaneous income not tied to a guest's rent/deposit/electricity (e.g. scrap sale, parking, vending). Guest is optional — add a Description for non-guest receipts.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Category</Label>
                    <button type="button" onClick={() => setManageCatOpen(v => !v)} className="text-xs text-blue-700 hover:underline" data-testid="manage-categories-toggle">
                      {manageCatOpen ? 'Done' : 'Manage'}
                    </button>
                  </div>
                  <Select value={form.other_category} onValueChange={(v) => setForm({ ...form, other_category: v })}>
                    <SelectTrigger data-testid="other-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {otherCategories.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={form.payment_date} 
                    onChange={(e) => setForm({ ...form, payment_date: e.target.value })} 
                    data-testid="payment-date"
                  />
                </div>
              </div>

              {manageCatOpen && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="manage-categories-panel">
                  <div className="flex gap-2">
                    <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" data-testid="new-category-input" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }} />
                    <Button type="button" variant="outline" onClick={handleAddCategory} disabled={savingCats} data-testid="add-category-btn">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {otherCategories.map(c => (
                      <span key={c} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs">
                        {c}
                        <button type="button" onClick={() => handleRemoveCategory(c)} className="text-slate-400 hover:text-rose-600" data-testid={`remove-category-${c}`}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Guest <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Select value={form.guest_id} onValueChange={handleGuestSelect}>
                    <SelectTrigger data-testid="payment-guest-select">
                      <SelectValue placeholder="None (non-guest)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (non-guest)</SelectItem>
                      {guests.map(g => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name} - {g.room_info} ({g.status?.replace('_', ' ')})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description / Remark <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value.toUpperCase() })} placeholder="EXTRA NOTE (DEFAULTS TO CATEGORY)" data-testid="payment-remarks-input" />
              </div>

              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-emerald-600" /> Cash (₹)</Label>
                  <Input type="number" min="0" value={form.cash_other} onChange={(e) => setForm({ ...form, cash_other: parseFloat(e.target.value) || 0 })} data-testid="payment-cash-other" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-blue-600" /> Bank (₹)</Label>
                  <Input type="number" min="0" value={form.bank_other} onChange={(e) => setForm({ ...form, bank_other: parseFloat(e.target.value) || 0 })} data-testid="payment-bank-other" />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-payment-btn">
                  Record Receipt
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          {properties.length > 1 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[190px]" data-testid="receipts-property-select">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Edit Receipt Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Receipt - {editingPayment?.guest_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest</Label>
                <Input value={`${editingPayment?.guest_name} - ${editingPayment?.room_info || ''}`} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={editForm.payment_date} 
                  onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })} 
                  data-testid="edit-receipt-date"
                />
              </div>
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              The <b>head</b> is locked to keep your ledger accurate. You can change the amount, mode and date here. To move this money to a different head, delete this entry and record it again under the correct head.
            </div>

            <div className="grid grid-cols-3 gap-4 border-t pt-4">
              <div className="space-y-2">
                <Label>Head</Label>
                <Input value={_headLabel(editingPayment?.payment_type)} disabled className="bg-slate-50 font-semibold" data-testid="edit-receipt-head" />
              </div>
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })}
                  data-testid="edit-receipt-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Mode</Label>
                <Select value={editForm.payment_mode} onValueChange={(v) => setEditForm({ ...editForm, payment_mode: v })}>
                  <SelectTrigger data-testid="edit-receipt-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value.toUpperCase() })} placeholder="OPTIONAL REMARKS..." />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="edit-receipt-submit">
                <Save className="h-4 w-4 mr-1" /> Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="daily-tab">
            <CalendarDays className="h-4 w-4" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="monthly-tab">
            <Calendar className="h-4 w-4" />
            Monthly
          </TabsTrigger>
          <TabsTrigger value="lifetime" className="flex items-center gap-2" data-testid="lifetime-tab">
            <History className="h-4 w-4" />
            Lifetime
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[200px]"
            data-testid="receipt-date-filter"
          />
        </TabsContent>

        <TabsContent value="monthly" className="mt-4">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-[200px]"
            data-testid="receipt-month-filter"
          />
        </TabsContent>

        <TabsContent value="lifetime" className="mt-4">
          <p className="text-sm text-slate-500">Showing all receipts</p>
        </TabsContent>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-emerald-200" style={{ animationDelay: '0ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><Wallet className="h-4 w-4 text-emerald-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Cash Receipts</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-emerald-700">₹{totals.cash.toLocaleString()}</p>
          <div className="mt-2 text-xs text-slate-500 space-y-1">
            <div className="flex justify-between"><span>Rent</span><span className="font-mono">₹{totals.cash_rent.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Deposit</span><span className="font-mono">₹{totals.cash_deposit.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Electricity</span><span className="font-mono">₹{totals.cash_electricity.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Other</span><span className="font-mono">₹{totals.cash_other.toLocaleString()}</span></div>
          </div>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-blue-200" style={{ animationDelay: '60ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><CreditCard className="h-4 w-4 text-blue-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Bank Receipts</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-blue-700">₹{totals.bank.toLocaleString()}</p>
          <div className="mt-2 text-xs text-slate-500 space-y-1">
            <div className="flex justify-between"><span>Rent</span><span className="font-mono">₹{totals.bank_rent.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Deposit</span><span className="font-mono">₹{totals.bank_deposit.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Electricity</span><span className="font-mono">₹{totals.bank_electricity.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Other</span><span className="font-mono">₹{totals.bank_other.toLocaleString()}</span></div>
          </div>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-slate-900 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" style={{ animationDelay: '120ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-20"></div>
          <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="h-4 w-4 text-slate-400" /><span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Total Receipts</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-white">₹{totals.total.toLocaleString()}</p>
          <div className="mt-2 text-xs text-slate-400 space-y-1">
            <div className="flex justify-between"><span>Rent</span><span className="font-mono">₹{totals.rent.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Deposit</span><span className="font-mono">₹{totals.deposit.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Electricity</span><span className="font-mono">₹{totals.electricity.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Other</span><span className="font-mono">₹{totals.other.toLocaleString()}</span></div>
          </div>
        </div>
      </div>

      {/* Other Income by Category */}
      {(() => {
        const byCat = {};
        payments.filter(p => p.payment_type === 'other').forEach(p => {
          const c = p.other_category || 'Uncategorised';
          byCat[c] = (byCat[c] || 0) + p.amount;
        });
        const cats = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
        if (cats.length === 0) return null;
        return (
          <Card className="dash-animate relative overflow-hidden" data-testid="other-by-category-card" style={{ animationDelay: '200ms' }}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 z-10" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-600" /> Other Income by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {cats.map(([c, amt]) => (
                  <div key={c} className="rounded-xl bg-cyan-50 border border-cyan-100 px-3 py-2" data-testid={`other-cat-${c}`}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium truncate">{c}</p>
                    <p className="text-lg font-bold font-mono text-cyan-700">₹{amt.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Search */}
      <div className="relative w-full sm:w-[280px]">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search guest or room..." className="pl-9" data-testid="receipt-search-input" />
      </div>

      {/* Receipts Table */}
      <Card className="dash-animate relative overflow-hidden" data-testid="receipts-table" style={{ animationDelay: '220ms' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 z-10" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            Receipt Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead className="text-right">Cash Rent</TableHead>
                  <TableHead className="text-right">Cash Dep.</TableHead>
                  <TableHead className="text-right">Cash Elec.</TableHead>
                  <TableHead className="text-right">Bank Rent</TableHead>
                  <TableHead className="text-right">Bank Dep.</TableHead>
                  <TableHead className="text-right">Bank Elec.</TableHead>
                  <TableHead className="text-right">Cash Other</TableHead>
                  <TableHead className="text-right">Bank Other</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-14">
                      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                        <Banknote className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-1">No receipts recorded</h3>
                      <p className="text-slate-500 mb-5 text-sm">Record a guest payment to start tracking your receipts.</p>
                      {canAdd('receipts') && (
                        <Button onClick={() => setDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="empty-record-receipt-btn">
                          <Plus className="w-4 h-4 mr-2" /> Record Receipt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : visiblePayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-12">
                      <Search className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <p className="font-medium text-slate-700">No receipts match your search</p>
                    </TableCell>
                  </TableRow>
                ) : visiblePayments.map((payment) => {
                  // Calculate values for each column based on payment type and mode
                  const cashRent = payment.payment_mode === 'cash' && payment.payment_type === 'rent' ? payment.amount : 0;
                  const cashDeposit = payment.payment_mode === 'cash' && (payment.payment_type === 'deposit' || payment.payment_type === 'booking_deposit') ? payment.amount : 0;
                  const cashElectricity = payment.payment_mode === 'cash' && payment.payment_type === 'electricity' ? payment.amount : 0;
                  const bankRent = payment.payment_mode === 'bank' && payment.payment_type === 'rent' ? payment.amount : 0;
                  const bankDeposit = payment.payment_mode === 'bank' && (payment.payment_type === 'deposit' || payment.payment_type === 'booking_deposit') ? payment.amount : 0;
                  const bankElectricity = payment.payment_mode === 'bank' && payment.payment_type === 'electricity' ? payment.amount : 0;
                  const cashOther = payment.payment_mode === 'cash' && payment.payment_type === 'other' ? payment.amount : 0;
                  const bankOther = payment.payment_mode === 'bank' && payment.payment_type === 'other' ? payment.amount : 0;
                  const rowTotal = payment.amount;
                  
                  // Determine guest type for display - use display_type for consistent status across app
                  // For booking deposits (no guest yet), show as "booked"
                  let displayType = 'occupied';
                  if (payment.payment_type === 'other' && !payment.guest_id) {
                    displayType = 'other';
                  } else if (payment.booking_id || payment.payment_type === 'booking_deposit') {
                    displayType = 'booked';
                  } else {
                    const guest = guests.find(g => g.id === payment.guest_id);
                    displayType = guest?.display_type || guest?.status || 'occupied';
                  }
                  
                  // Status badge styling (consistent with Guest List, Outstanding Dues, Ledger)
                  const getStatusBadge = (type) => {
                    const variants = {
                      booked: 'bg-blue-100 text-blue-700',
                      new_join: 'bg-emerald-100 text-emerald-700',
                      occupied: 'bg-slate-100 text-slate-700',
                      notice: 'bg-amber-100 text-amber-700',
                      shifting: 'bg-purple-100 text-purple-700',
                      left: 'bg-rose-100 text-rose-700',
                      joined: 'bg-emerald-100 text-emerald-700',
                      day_basis: 'bg-purple-100 text-purple-700',
                      overstay: 'bg-red-500 text-white',
                      other: 'bg-cyan-100 text-cyan-700'
                    };
                    const labels = {
                      booked: 'Booked',
                      new_join: 'Joined',
                      occupied: 'Occupied',
                      notice: 'Notice',
                      shifting: 'Shifting',
                      left: 'Left',
                      joined: 'Joined',
                      day_basis: 'Day Basis',
                      overstay: 'Overstay',
                      other: 'Other'
                    };
                    return { style: variants[type] || 'bg-slate-100 text-slate-700', label: labels[type] || type };
                  };
                  const statusInfo = getStatusBadge(displayType);
                  
                  return (
                    <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`} className="transition-colors hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{formatDate(payment.payment_date)}</TableCell>
                      <TableCell>{payment.guest_name || payment.remarks || 'Other Receipt'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.style}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60"></span>
                          {statusInfo.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{payment.room_info}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{cashRent || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{cashDeposit || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{cashElectricity || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{bankRent || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{bankDeposit || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{bankElectricity || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{cashOther || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{bankOther || '-'}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">₹{rowTotal.toLocaleString()}</TableCell>
                      <TableCell className="text-center">
                        {(canEdit('receipts') || canDelete('receipts')) && (
                          <div className="flex justify-center gap-1">
                            {canEdit('receipts') && (
                              <Button variant="ghost" size="icon" onClick={() => handleEditClick(payment)} className="h-7 w-7" data-testid={`edit-payment-${payment.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canDelete('receipts') && (
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(payment.id)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-payment-${payment.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
